<template>
  <div class="anchor-navigation">
    <nav>
      <h5>{{ $t('goTo') }}:</h5>

      <a v-if="showComments"
         @click="navigateTo('Comments')">
        <pills-tag :text="$tc('comment', 2)"
                   text-color="is-info"
                   :display-empty-pill="true"
                   :total="content.comments.total" />
      </a>

      <a v-if="showRelated"
         @click="navigateTo('Related')">
        <pills-tag :text="$t('related')"
                   text-color="is-info"
                   :display-empty-pill="true"
                   :total="content.related.total" />
      </a>

      <a v-if="showAttachments"
         @click="navigateTo('Attachments')">
        <pills-tag :text="$tc('attachment', 2)"
                   text-color="is-info"
                   :display-empty-pill="true"
                   :total="content.attachments.total" />
      </a>

      <a v-if="showWhereUsed"
         @click="navigateTo('WhereUsed')">
        <pills-tag :text="$t('whereUsed')"
                   text-color="is-info"
                   :display-empty-pill="true"
                   :total="content.whereused.total" />
      </a>

      <a v-if="showSuggestions"
         @click="navigateTo('Suggestions')">
        <pills-tag :text="$tc('suggestion', 2)"
                   text-color="is-info"
                   :display-empty-pill="true"
                   :total="content.suggestions.total" />
      </a>

      <div v-if="showPartCollections"
           class="part-collections-menu"
           @mouseenter="partCollectionsOpen = true"
           @mouseleave="partCollectionsOpen = false">
        <a @click="navigateTo('PartCollections')">
          <pills-tag :text="$tc('partCollection', 2)"
                     text-color="is-info"
                     :display-empty-pill="true"
                     :total="content.partCollectionsCount ?? 0" />
        </a>

        <div v-if="partCollectionsOpen && collections.length"
            class="part-collections-dropdown"
            @scroll="handleDropdownScroll">
          <button v-for="collection in collections"
                  :key="collection.id"
                  class="dropdown-item-button"
                  @click.stop="handleCollectionSelection(collection.id)">
            {{ collection.name }}
          </button>

          <div v-if="isLoadingMoreCollections" class="dropdown-loading">
            {{ $t('loading') }}...
          </div>
        </div>
      </div>
    </nav>
  </div>

  <div v-if="!hideBanner" class="where-used-banner">
    <span class="where-used-banner-text">
      {{ $t('partVerificationInstructions') }}
    </span>

    <button class="button where-used-banner-button"
            @click="navigateTo('WhereUsed')">
      <span class="where-used-banner-button-text">
        {{ $t('viewWhereUsed') }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import useRouteParams from '@/composables/useRouteParams'
import useDetailPage from './useDetailPage'
import { usePartCollectionsData } from '@/composables/collections/usePartCollectionsData'
import { usePartCollectionSearch } from '@/composables/collections/usePartCollectionSearch'

import PillsTag from '@/components/common/PillsTag.vue'

const store = useStore()
const { id } = useRouteParams()

const {
  content,
  isPart,
  showComments,
  showRelated,
  showSuggestions,
  showWhereUsed,
  showAttachments,
  showPartCollections
} = useDetailPage()

const {
  clearSearch: clearPartCollectionSearch
} = usePartCollectionSearch(
  id.value,
  () => {}
)

const partCollectionsOpen = ref(false)

const isPagePart = computed(() => isPart.value && content.value.pagePartId !== '')

const showPartInformationBanner = computed(() => store.state.user.showPartInformationBanner)

const hideBanner = computed(() =>
  (!isPart.value || isPagePart.value) ||
  !showPartInformationBanner.value ||
  (store.state.user.hidePagesInSearch && store.state.user.hideBooksInSearch)
)

/**
 * AnchorNavigation is only consuming shared state here.
 * It must not call loadCollections() or resetIfOwner().
 */
const {
  collections,
  selectCollection,
  loadMoreCollections,
  hasMoreCollections,
  isLoadingMoreCollections
} = usePartCollectionsData(
  id.value,
  (part: any) => part
)

function isNearBottom(el: HTMLElement, threshold = 48) {
  return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
}

async function handleDropdownScroll(event: Event) {
  const el = event.target as HTMLElement | null
  if (!el) return
  if (!hasMoreCollections.value) return
  if (isLoadingMoreCollections.value) return
  if (!isNearBottom(el)) return

  await loadMoreCollections()
}

const navigateTo = (anchor: string) => {
  switch (anchor) {
    case 'Related':
      document.getElementById(anchor)?.scrollIntoView({ block: 'center' })
      break
    default:
      document.getElementById(anchor)?.scrollIntoView()
  }
}

const handleCollectionSelection = (collectionId: number) => {
  clearPartCollectionSearch()
  navigateTo('PartCollections')
  selectCollection(collectionId)
  partCollectionsOpen.value = false
}
</script>

<style scoped lang="scss">
.anchor-navigation nav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.part-collections-menu {
  position: relative;
  display: inline-block;
  padding-bottom: 6px;
  margin-bottom: -6px;
}

.part-collections-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 220px;
  max-width: 320px;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  padding: 0.25rem 0;
}

.dropdown-item-button {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.9rem;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.dropdown-item-button:hover {
  background: #f5f5f5;
}

.where-used-banner {
  border: 2px solid;
  border-radius: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  background-color: var(--banners);
  color: var(--banners--color-invert);
  border-color: var(--banners--dark-color);
  padding: 5px;

  @media (max-width: 500px) {
    justify-content: left;
  }
}

.where-used-banner-text {
  font-weight: bold;
  margin-right: 1rem;
  font-size: 1.10rem;

  @media (max-width: 500px) {
    width: 75%;
  }
}

.where-used-banner-button {
  background-color: var(--banners--color-invert) !important;
  border-color: var(--banners--80--rgba) !important;
  color: var(--banners--color-invert-text) !important;
  height: 1.75rem;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.6) !important;

  @media (max-width: 500px) {
    width: 75%;
  }

  @media (max-width: 300px) {
    width: 50%;
  }

  &:focus {
    outline: none !important;
  }
}

.where-used-banner-button-text {
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>